import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useMemo, useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useI18n } from "@/contexts/i18n";
import { useForm, FormProvider } from "react-hook-form";
import { validationSchema, hasCompleteUserInfo, getFilteredObject, } from "@/utils";
import { useLeadValue, useModal, usePrefill, useTrackingLeadInterestMail, useFeatureEnabled, useTrackingLegacyInterest, } from "@/hooks";
import { ModalIds } from "@/constants";
import { useClassified } from "../classified";
import { usePrefillDataCookie } from "@/components/contacting/ContactForm/usePrefillDataCookie";
import { useGDPR } from "../GDPR";
import { useFeaturesEnabled } from "../cdpConfig";
import { useBrandContext } from "@/shared/contexts";
export const ContactContext = createContext(null);
export const ContactProvider = ({ children, contactSections, crsBaseUrl, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const { cmp } = useBrandContext();
    const { isOpen, toggle, closeAndReplace } = useModal(false, ModalIds.CONTACT);
    const { isOpen: suggestPriceIsOpen, toggle: toggleSuggestPricePriceModal } = useModal(false, ModalIds.PRICE);
    const legacyTrackingData = (_a = useClassified()) === null || _a === void 0 ? void 0 : _a.legacyTracking;
    const hasLegacyTrackingData = !!legacyTrackingData;
    const locationEnabled = (_d = (_c = (_b = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _b === void 0 ? void 0 : _b.customFields) === null || _c === void 0 ? void 0 : _c.locationEnabled) !== null && _d !== void 0 ? _d : false;
    const isOpenContactForm = useFeaturesEnabled().openedContactForm;
    const { t } = useI18n();
    const [defaultMessage, setDefaultMessage] = useState(t("contact.form.subject_placeholder"));
    const [userData, setUserData] = useState(null);
    const [userCookieData, setUserCookieData] = useState(null);
    const [userInfoComplete, setUserInfoComplete] = useState(false);
    const [showImageHeaderSection, setShowImageHeaderSection] = useState(false);
    const isPhoneNumberRequired = useFeatureEnabled("phoneNumberRequired");
    const isMessageRequired = useFeatureEnabled("messageRequired");
    const isPrivateOwner = (_f = (_e = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _e === void 0 ? void 0 : _e.contactCard.isPrivateOwner) !== null && _f !== void 0 ? _f : false;
    const { getPrefillDataCookie, erasePrefillDataCookie } = usePrefillDataCookie();
    const { purposesConsent, isGDPRReady } = useGDPR();
    const [avZone, setAvZone] = useState(null);
    const deletePrefillDataCookie = () => {
        const GDPRType = cmp === null || cmp === void 0 ? void 0 : cmp.type;
        const cookieConsent = GDPRType === "didomi" ? "personnali-FDjDrWrj" : "Contact Auto-fill";
        if (!purposesConsent.includes(cookieConsent)) {
            erasePrefillDataCookie();
        }
    };
    const methods = useForm({
        resolver: zodResolver(validationSchema(t, (_h = (_g = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _g === void 0 ? void 0 : _g.displayOwnerFlag) !== null && _h !== void 0 ? _h : false, (_l = (_k = (_j = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _j === void 0 ? void 0 : _j.customFields) === null || _k === void 0 ? void 0 : _k.locationEnabled) !== null && _l !== void 0 ? _l : false, isPhoneNumberRequired, isMessageRequired, isPrivateOwner, suggestPriceIsOpen)),
        mode: "onBlur",
        defaultValues: {
            message: defaultMessage,
        },
        shouldFocusError: isOpenContactForm ? false : true,
    });
    useEffect(() => {
        if (isGDPRReady) {
            deletePrefillDataCookie();
            const prefillDataCookie = getPrefillDataCookie();
            if (prefillDataCookie) {
                setUserCookieData(prefillDataCookie);
            }
            else {
                setUserCookieData(null);
            }
        }
        // we want to run the Effect just once the GDPR is ready
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGDPRReady]);
    const isSeekerProfileForRentalOnly = useFeatureEnabled("seekerProfileForRentalOnly");
    const isRent = ((_o = (_m = useClassified()) === null || _m === void 0 ? void 0 : _m.tracking) === null || _o === void 0 ? void 0 : _o.av_distribution_type_global) === "1";
    // FIXME remove this logic when this is handled on the bff (https://avivgroup.atlassian.net/browse/ASC-3334)
    const enableShareSeekerProfile = !isSeekerProfileForRentalOnly ||
        (isSeekerProfileForRentalOnly && isRent && isPrivateOwner);
    usePrefill({
        crsBaseUrl,
        onLoad: (userData) => {
            if (userData) {
                const locationKeys = [
                    "city",
                    "street",
                    "zipCode",
                ];
                const excludedKeys = locationEnabled ? [] : locationKeys;
                const filteredUserData = getFilteredObject(userData, excludedKeys);
                setUserData(filteredUserData);
            }
            setUserInfoComplete(hasCompleteUserInfo(userData));
        },
    });
    const { fetchLeadValue } = useFeaturesEnabled();
    const leadValue = useLeadValue({
        crsBaseUrl,
        legacyId: contactSections === null || contactSections === void 0 ? void 0 : contactSections.legacyId,
        shouldSendLeadValue: fetchLeadValue &&
            isOpen &&
            !!((_p = contactSections === null || contactSections === void 0 ? void 0 : contactSections.contactForm) === null || _p === void 0 ? void 0 : _p.shouldSendLeadValue),
    });
    const [tracking, setTracking] = useState(null);
    const trackLeadInterestMail = useTrackingLeadInterestMail({
        av_zone: tracking === null || tracking === void 0 ? void 0 : tracking.av_zone,
        av_feature: tracking === null || tracking === void 0 ? void 0 : tracking.av_feature,
        av_contact_info_field: userInfoComplete ? "completed" : "not_completed",
    });
    const trackingLegacyInterest = useTrackingLegacyInterest({
        type: "mail",
        infos: tracking === null || tracking === void 0 ? void 0 : tracking.legacyTrackingInfos,
    });
    const [contactTrackingSent, setContactTrackingSent] = useState(false);
    const [suggestPriceTrackingSent, setSuggestPriceTrackingSent] = useState(false);
    useEffect(() => {
        if (isOpen && tracking && !contactTrackingSent) {
            trackLeadInterestMail();
            if (hasLegacyTrackingData)
                trackingLegacyInterest(); //legacy GA3 for harvest
            setContactTrackingSent(true);
        }
        if (suggestPriceIsOpen && tracking && !suggestPriceTrackingSent) {
            trackLeadInterestMail();
            if (hasLegacyTrackingData)
                trackingLegacyInterest();
            setSuggestPriceTrackingSent(true);
        }
    }, [
        isOpen,
        suggestPriceIsOpen,
        trackLeadInterestMail,
        trackingLegacyInterest, //legacy GA3 for harvest
        tracking,
        contactTrackingSent,
        suggestPriceTrackingSent,
        hasLegacyTrackingData,
    ]);
    const toggleModal = useCallback((trackingState) => {
        setAvZone(trackingState.av_zone);
        toggle();
        if (trackingState) {
            setTracking(trackingState);
        }
    }, [toggle, setTracking]);
    const toggleSuggestPriceModal = useCallback((trackingState) => {
        setAvZone(trackingState.av_zone);
        toggleSuggestPricePriceModal();
        if (trackingState) {
            setTracking(trackingState);
        }
    }, [toggleSuggestPricePriceModal, setTracking]);
    const triggerImageHeaderSection = useCallback(() => {
        setShowImageHeaderSection(true);
    }, []);
    const resetImageHeaderSection = useCallback(() => {
        setShowImageHeaderSection(false);
    }, []);
    const closeModal = useCallback(() => {
        resetImageHeaderSection();
        closeAndReplace();
        setTracking(null);
        setContactTrackingSent(false);
        setSuggestPriceTrackingSent(false);
    }, [closeAndReplace, setTracking, resetImageHeaderSection]);
    const contextValue = useMemo(() => ({
        contactTracking: {
            ...(fetchLeadValue && leadValue && { leadValue }),
            trackingLegacyInfos: tracking === null || tracking === void 0 ? void 0 : tracking.legacyTrackingInfos, //legacy GA3 for harvest
        },
        contactSections,
        toggleModal,
        suggestPriceIsOpen,
        toggleSuggestPriceModal,
        avZone,
        closeModal,
        isOpen,
        setDefaultMessage,
        defaultMessage,
        userInfoComplete,
        userData,
        userCookieData,
        enableShareSeekerProfile,
        showImageHeaderSection,
        triggerImageHeaderSection,
        resetImageHeaderSection,
    }), [
        contactSections,
        toggleModal,
        suggestPriceIsOpen,
        toggleSuggestPriceModal,
        avZone,
        closeModal,
        isOpen,
        leadValue,
        setDefaultMessage,
        defaultMessage,
        userInfoComplete,
        userData,
        userCookieData,
        tracking,
        enableShareSeekerProfile,
        fetchLeadValue,
        showImageHeaderSection,
        triggerImageHeaderSection,
        resetImageHeaderSection,
    ]);
    return (_jsx(ContactContext.Provider, { value: contextValue, children: _jsx(FormProvider, { ...methods, children: children }) }));
};
